import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Lock, ShieldCheck, AlertTriangle } from 'lucide-react';
import { useCart } from './CartContext';
const MAIN_IMAGE = 'https://cdn.magicpatterns.com/uploads/gPBztpZ4dzY3epEKqjdKN6/8PXa8N2_(2).png';
const MAIN_VARIANT = '42439418740821';
const MAIN_PRICE = 10.0;
const MAIN_COMPARE = 29.99;
const BUMPS = [{
  id: 'b0',
  variant: '42445179158613',
  title: 'Guía Práctica del Variador de Frecuencia',
  description: 'Aprende a instalar, configurar y dominar variadores desde cero. Evita errores costosos y trabaja como un profesional desde el primer día.',
  trigger: '👉 Recomendado para mejores resultados',
  price: 7,
  comparePrice: 17.5,
  discount: 60,
  image: 'https://cdn.magicpatterns.com/uploads/ieFvauTLdjYf95g9APnMtq/GuiadeComandosElectricos.png'
}, {
  id: 'b1',
  variant: '42445195706453',
  title: 'Programación de PLC + Ejercicios Reales',
  description: 'Domina la programación industrial paso a paso y lleva tu nivel técnico a otro nivel con ejemplos reales listos para aplicar.',
  trigger: '👉 Perfecto para llevarlo al siguiente nivel.',
  price: 15.95,
  comparePrice: 25,
  discount: 60,
  image: 'https://cdn.magicpatterns.com/uploads/tQECKf9Li3Pqsm7naoTeFu/CapturadeTela20251007as193509.png'
}];
export function CartDrawer() {
  const {
    isCartOpen,
    closeCart
  } = useCart();
  const [timeLeft, setTimeLeft] = useState(15 * 60);
  const [bumpSelected, setBumpSelected] = useState<Record<string, boolean>>({
    b0: false,
    b1: false
  });
  const toggleBump = (id: string) => {
    setBumpSelected(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const totalPrice = MAIN_PRICE + BUMPS.reduce((sum, bump) => sum + (bumpSelected[bump.id] ? bump.price : 0), 0);
  const totalSavings = MAIN_COMPARE - MAIN_PRICE + BUMPS.reduce((sum, bump) => sum + (bumpSelected[bump.id] ? bump.comparePrice - bump.price : 0), 0);
  const getCheckoutUrl = () => {
    const items = [`${MAIN_VARIANT}:1`];
    BUMPS.forEach(bump => {
      if (bumpSelected[bump.id] && !bump.variant.startsWith('TU_VARIANT')) {
        items.push(`${bump.variant}:1`);
      }
    });
    return `https://recetariosaludable.lat/cart/${items.join(',')}?checkout`;
  };
  useEffect(() => {
    if (!isCartOpen) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, [isCartOpen]);
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  const fmt = (amount: number) => `$${amount.toFixed(2)}`;
  const handleCheckout = () => {
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'InitiateCheckout', {
        content_name: 'Manual de Controles Eléctricos + Guía Práctica',
        content_type: 'product',
        value: totalPrice,
        currency: 'USD'
      });
    }
    window.open(getCheckoutUrl(), '_blank');
  };
  return <AnimatePresence data-id="element-61">
      {isCartOpen && <>
          {/* Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={closeCart} className="fixed inset-0 bg-black/40 z-[99998]" data-id="element-62" />

          {/* Drawer */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 28,
        stiffness: 220
      }} className="fixed right-0 top-0 h-full w-full md:max-w-[420px] bg-[#f7f7f7] text-[#111] z-[99999] flex flex-col shadow-[-4px_0_24px_rgba(0,0,0,0.1)] font-[Inter,_sans-serif]" data-id="element-63">
            {/* ── HEADER ── */}
            <div className="flex justify-between items-center px-5 py-3.5 bg-white border-b border-gray-200" data-id="element-64">
              <h2 className="font-extrabold text-[17px] tracking-tight text-[#111]" data-id="element-65">
                Tu Pedido
              </h2>
              <button onClick={closeCart} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" data-id="element-66">
                <X className="w-5 h-5" data-id="element-67" />
              </button>
            </div>

            {/* ── URGENCY TIMER ── */}
            <div className="bg-[#FFF3E0] border-b border-[#FFE0B2] text-center py-2.5 px-4" data-id="element-68">
              <span className="text-[#E65100] text-[13px] font-semibold" data-id="element-69">
                ⏳ Esta oferta expira en{' '}
                <span className="font-black text-[15px] text-[#BF360C]" data-id="element-70">
                  {formatTime(timeLeft)}
                </span>
              </span>
            </div>

            {/* ── BODY ── */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-id="element-71">
              {/* ═══ MAIN PRODUCT CARD ═══ */}
              <div className="bg-white rounded-xl shadow-[0_1px_8px_rgba(0,0,0,0.06)] border border-gray-100 p-4" data-id="element-72">
                {/* Added badge */}
                <div className="inline-flex items-center gap-1.5 bg-[#E8F5E9] text-[#2E7D32] text-[11px] font-bold px-2.5 py-1 rounded-full mb-3 uppercase tracking-wider" data-id="element-73">
                  <Check className="w-3 h-3" strokeWidth={3} data-id="element-74" />
                  Agregado al pedido
                </div>

                <div className="flex gap-4" data-id="element-75">
                  <img src={MAIN_IMAGE} alt="Manual de Controles Eléctricos" className="w-[90px] h-[64px] object-contain rounded-lg flex-shrink-0" data-id="element-76" />
                  <div className="flex-1 min-w-0" data-id="element-77">
                    <h3 className="text-[14px] font-extrabold leading-tight text-[#111] mb-1" data-id="element-78">
                      Manual de Controles Eléctricos + Guía Práctica
                    </h3>
                    <p className="text-[11px] text-[#888] font-medium mb-2 leading-snug" data-id="element-79">
                      Todo lo que necesitas para dominar comandos eléctricos
                    </p>
                    <div className="flex items-baseline gap-2" data-id="element-80">
                      <span className="font-black text-[22px] text-[#111] leading-none" data-id="element-81">
                        {fmt(MAIN_PRICE)}
                      </span>
                      <span className="text-[#bbb] line-through text-[13px]" data-id="element-82">
                        {fmt(MAIN_COMPARE)}
                      </span>
                      <span className="bg-[#FFEBEE] text-[#C62828] text-[10px] font-bold px-1.5 py-0.5 rounded" data-id="element-83">
                        -67%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ═══ BUMPS SECTION ═══ */}
              {/* Psychology trigger */}
              <div className="flex items-start gap-2 bg-[#FFF8E1] border border-[#FFE082] rounded-lg px-3.5 py-2.5" data-id="element-84">
                <AlertTriangle className="w-4 h-4 text-[#F9A825] flex-shrink-0 mt-0.5" data-id="element-85" />
                <p className="text-[12px] font-semibold text-[#5D4037] leading-snug" data-id="element-86">
                  Mejora tu pedido con estos complementos opcionales
                </p>
              </div>

              {/* Bump cards */}
              {BUMPS.map(bump => {
            const isSelected = bumpSelected[bump.id];
            return <motion.div key={bump.id} onClick={() => toggleBump(bump.id)} whileTap={{
              scale: 0.99
            }} className={`bg-white rounded-xl shadow-[0_1px_8px_rgba(0,0,0,0.06)] cursor-pointer transition-all duration-200 overflow-hidden ${isSelected ? 'border-2 border-[#22C55E] shadow-[0_0_0_3px_rgba(34,197,94,0.1),0_2px_12px_rgba(0,0,0,0.06)]' : 'border border-gray-100 hover:border-gray-200'}`} data-id="element-87">
                    {/* Card content */}
                    <div className="p-4" data-id="element-88">
                      <div className="flex gap-3.5" data-id="element-89">
                        {/* Image */}
                        <img src={bump.image} alt={bump.title} className="w-[68px] h-[68px] object-cover rounded-lg flex-shrink-0 border border-gray-100" data-id="element-90" />

                        {/* Info */}
                        <div className="flex-1 min-w-0" data-id="element-91">
                          <h4 className="text-[13px] font-extrabold text-[#111] leading-tight mb-0.5" data-id="element-92">
                            {bump.title}
                          </h4>
                          <p className="text-[10px] text-[#C62828] font-semibold mb-1.5 italic" data-id="element-93">
                            Disponible solo durante esta compra
                          </p>
                          <p className="text-[11px] text-[#666] font-medium leading-snug mb-2" data-id="element-94">
                            {bump.description}
                          </p>

                          {/* Price row */}
                          <div className="flex items-baseline gap-2" data-id="element-95">
                            <span className="font-black text-[17px] text-[#111] leading-none" data-id="element-96">
                              {fmt(bump.price)}
                            </span>
                            <span className="text-[#bbb] line-through text-[12px]" data-id="element-97">
                              {fmt(bump.comparePrice)}
                            </span>
                            <span className="bg-[#FFEBEE] text-[#C62828] text-[10px] font-bold px-1.5 py-0.5 rounded" data-id="element-98">
                              -{bump.discount}%
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Emotional trigger */}
                      <p className="text-[11px] text-[#E65100] font-semibold mt-3 leading-snug bg-[#FFF3E0] rounded-md px-3 py-2" data-id="element-99">
                        {bump.trigger}
                      </p>
                    </div>

                    {/* Add to order row */}
                    <div className={`flex items-center gap-3 px-4 py-3 transition-colors ${isSelected ? 'bg-[#E8F5E9]' : 'bg-[#FAFAFA] border-t border-gray-100'}`} data-id="element-100">
                      <div className={`w-[20px] h-[20px] rounded flex items-center justify-center flex-shrink-0 transition-all duration-200 ${isSelected ? 'bg-[#22C55E] shadow-[0_0_6px_rgba(34,197,94,0.35)]' : 'border-2 border-gray-300 bg-white'}`} data-id="element-101">
                        {isSelected && <motion.div initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 15
                  }} data-id="element-102">
                            <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-103" />
                          </motion.div>}
                      </div>
                      <span className={`${`${`text-[13px] font-bold transition-colors ${isSelected ? 'text-[#2E7D32]' : 'text-[#666]'}`} font-[900] text-[#0D0C0C]`} font-[900] text-[#090808]`} data-id="element-104">
                        {isSelected ? '✔ Añadido a mi pedido' : 'Sí, quiero añadirlo'}
                      </span>
                    </div>
                  </motion.div>;
          })}

              <div className="h-1" data-id="element-105"></div>
            </div>

            {/* ── FOOTER ── */}
            <div className="bg-white border-t border-gray-200 px-5 py-4 shadow-[0_-2px_12px_rgba(0,0,0,0.04)]" data-id="element-106">
              {/* You Save row */}
              <AnimatePresence mode="wait" data-id="element-107">
                {totalSavings > 0 && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="flex justify-between items-center mb-2" data-id="element-108">
                    <span className="text-[#888] text-[13px] font-medium" data-id="element-109">
                      Ahorras
                    </span>
                    <span className="text-[#C62828] text-[14px] font-bold" data-id="element-110">
                      -{fmt(totalSavings)}
                    </span>
                  </motion.div>}
              </AnimatePresence>

              {/* Total row */}
              <div className="flex justify-between items-center mb-4" data-id="element-111">
                <span className="text-[#111] text-[14px] font-bold" data-id="element-112">Total</span>
                <AnimatePresence mode="wait" data-id="element-113">
                  <motion.span key={totalPrice} initial={{
                scale: 0.85,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20
              }} className="text-[28px] font-black text-[#111] leading-none" data-id="element-114">
                    {fmt(totalPrice)}
                  </motion.span>
                </AnimatePresence>
              </div>

              {/* CTA Button */}
              <motion.button onClick={handleCheckout} whileHover={{
            scale: 1.01,
            y: -1
          }} whileTap={{
            scale: 0.98
          }} className="w-full bg-[#22C55E] hover:bg-[#1DB954] text-white font-black text-[16px] py-4 rounded-xl shadow-[0_4px_16px_rgba(34,197,94,0.3)] hover:shadow-[0_6px_24px_rgba(34,197,94,0.45)] transition-all duration-200 flex items-center justify-center gap-2.5" data-id="element-115">
                <Lock className="w-4.5 h-4.5" data-id="element-116" />
                Completar mi compra
              </motion.button>

              {/* Subtexto */}
              <p className="text-center text-[11px] mt-2 font-[900] text-[#030303]" data-id="element-117">
                Acceso inmediato · Pago único
              </p>

              {/* Trust badges */}
              <div className="flex items-center justify-center gap-4 mt-3 pt-3 border-t border-gray-100" data-id="element-118">
                <span className="flex items-center gap-1 text-[11px] text-[#777] font-medium" data-id="element-119">
                  <Check className="w-3.5 h-3.5 text-[#22C55E]" data-id="element-120" />
                  Acceso inmediato
                </span>
                <span className="flex items-center gap-1 text-[11px] text-[#777] font-medium" data-id="element-121">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#22C55E]" data-id="element-122" />
                  Pago 100% seguro
                </span>
                <span className="flex items-center gap-1 text-[11px] text-[#777] font-medium" data-id="element-123">
                  <Check className="w-3.5 h-3.5 text-[#22C55E]" data-id="element-124" />
                  Garantía 7 días
                </span>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}